import React from 'react';
import { Upload, BarChart3, RefreshCw, MessageSquare, Clock, Sparkles } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import type { SetupMethod } from '@/types/jobintelProfile';

interface Props {
  onSelect: (method: SetupMethod) => void;
  hasTailorWizProfile?: boolean;
  hasCareerClarityReport?: boolean;
}

const paths = [
  {
    method: 'documents_uploaded' as SetupMethod,
    icon: Upload,
    title: 'Upload My Documents',
    description: 'Upload your resume, worksheets, or LinkedIn export and we\'ll build your profile automatically.',
    time: '~30 seconds',
    recommended: true,
  },
  {
    method: 'career_clarity_report' as SetupMethod,
    icon: BarChart3,
    title: 'Use My Career Clarity Report',
    description: 'Pre-fill your profile from your Career Clarity Report results with real market intelligence.',
    time: '~2 minutes',
    recommended: true,
  },
  {
    method: 'tailorwiz_profile' as SetupMethod,
    icon: RefreshCw,
    title: 'Use My TailorWiz Profile',
    description: 'Pull in your existing TailorWiz data and fill any JobIntel-specific gaps.',
    time: '~1 minute',
    recommended: false,
  },
  {
    method: 'manual' as SetupMethod,
    icon: MessageSquare,
    title: 'Answer the Questions',
    description: 'Walk through a guided conversation to build your profile from scratch.',
    time: '~5 minutes',
    recommended: false,
  },
];

export function ProfileSetupPathSelector({ onSelect, hasTailorWizProfile, hasCareerClarityReport }: Props) {
  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <div className="text-center space-y-3">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[hsl(var(--primary-light))] text-primary text-sm font-medium">
          <Sparkles className="h-4 w-4" />
          JobIntel 360 Profile Setup
        </div>
        <h1 className="text-3xl font-bold text-foreground">Build Your Career Profile</h1>
        <p className="text-muted-foreground max-w-xl mx-auto">
          Your profile powers every report, recommendation, and job alert. The more complete it is, the better your results.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {paths.map((path) => {
          const Icon = path.icon;
          const isDisabled = path.method === 'tailorwiz_profile' && !hasTailorWizProfile;

          return (
            <Card
              key={path.method}
              className={`relative cursor-pointer transition-all duration-300 hover:shadow-lg hover:border-primary group ${
                path.recommended ? 'border-2 border-primary/30 ring-1 ring-primary/10' : 'border'
              } ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
              onClick={() => !isDisabled && onSelect(path.method)}
            >
              {path.recommended && (
                <div className="absolute -top-3 left-4 px-3 py-0.5 bg-primary text-primary-foreground text-xs font-semibold rounded-full">
                  Recommended
                </div>
              )}
              <CardContent className="p-6 space-y-4">
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-xl bg-[hsl(var(--primary))] text-primary-foreground group-hover:scale-110 transition-transform">
                    <Icon className="h-6 w-6" />
                  </div>
                  <div className="flex-1 space-y-1">
                    <h3 className="font-semibold text-lg text-foreground">{path.title}</h3>
                    <p className="text-sm text-muted-foreground">{path.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Clock className="h-3.5 w-3.5" />
                  {path.time}
                </div>
                {isDisabled && (
                  <p className="text-xs text-warning">No TailorWiz profile found. Complete one first.</p>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
